import { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';

// Initialize emailjs with your public key
// Replace YOUR_PUBLIC_KEY with your actual EmailJS public key
emailjs.init('Cm_xCPf9wetOsaUjS');

/**
 * Global function to dispatch the open-demo-modal event.
 * Any component can call this to trigger the modal.
 */
export const openDemoModal = () => {
    window.dispatchEvent(new Event('open-demo-modal'));
};

function DemoModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactno: '',
        company_name: '',
        description: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const handleOpen = () => setIsOpen(true);
        window.addEventListener('open-demo-modal', handleOpen);
        return () => window.removeEventListener('open-demo-modal', handleOpen);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            // Send email using emailjs
            await emailjs.send(
                'service_6guh9aj',
                'template_r7k9mfi',
                {
                    name: formData.name,
                    email: formData.email,
                    contactno: formData.contactno,
                    company_name: formData.company_name,
                    description: formData.description
                },
                'Cm_xCPf9wetOsaUjS'
            );

            setMessage({ 
                type: 'success', 
                text: 'Demo request submitted successfully! We will contact you soon.' 
            });
            
            // Reset form
            setFormData({
                name: '',
                email: '',
                contactno: '',
                company_name: '',
                description: ''
            });

            // Close modal after 2 seconds
            setTimeout(() => {
                setIsOpen(false);
                setMessage({ type: '', text: '' });
            }, 2000);
        } catch (error) {
            console.error('Failed to send email:', error);
            setMessage({ 
                type: 'error', 
                text: 'Failed to submit request. Please try again.' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-primary-950/60 backdrop-blur-sm transition-opacity"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            />

            {/* Modal Panel */}
            <div
                className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                {/* Header */}
                <div className="bg-gradient-to-br from-primary-800 to-accent-600 px-6 py-5 flex items-center justify-between text-white">
                    <h2 id="modal-title" className="font-heading font-bold text-xl">Request a Demo</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form
                    className="p-6 flex flex-col gap-4 font-sans text-sm"
                    onSubmit={handleSubmit}
                >
                    {message.text && (
                        <div className={`p-4 rounded-lg font-semibold ${
                            message.type === 'success' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                        }`}>
                            {message.text}
                        </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="name" className="font-semibold text-neutral-700">Name</label>
                        <input 
                            type="text" 
                            id="name" 
                            name="name" 
                            required 
                            value={formData.name}
                            onChange={handleInputChange}
                            disabled={isLoading}
                            className="input-base py-2" 
                            placeholder="Jane Doe" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="font-semibold text-neutral-700">Email</label>
                        <input 
                            type="email" 
                            id="email" 
                            name="email" 
                            required 
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={isLoading}
                            className="input-base py-2" 
                            placeholder="jane@company.com" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="contactno" className="font-semibold text-neutral-700">Contact No</label>
                        <input 
                            type="tel" 
                            id="contactno" 
                            name="contactno" 
                            required 
                            value={formData.contactno}
                            onChange={handleInputChange}
                            disabled={isLoading}
                            className="input-base py-2" 
                            placeholder="+1 (555) 000-0000" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="company_name" className="font-semibold text-neutral-700">Company Name</label>
                        <input 
                            type="text" 
                            id="company_name" 
                            name="company_name" 
                            required 
                            value={formData.company_name}
                            onChange={handleInputChange}
                            disabled={isLoading}
                            className="input-base py-2" 
                            placeholder="Acme Corp" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="description" className="font-semibold text-neutral-700">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            rows="3"
                            required
                            value={formData.description}
                            onChange={handleInputChange}
                            disabled={isLoading}
                            className="input-base py-2 resize-none"
                            placeholder="Tell us about your computer vision needs..."
                        />
                    </div>

                    <div className="mt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            disabled={isLoading}
                            className="btn btn-sm btn-ghost disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn btn-sm btn-primary disabled:opacity-50"
                        >
                            {isLoading ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default DemoModal;
